import apiClient from '../client';
import { API_ENDPOINTS } from '../endpoints';
import type {
  CompanyProjectFilters,
  CreateCompanyProjectRequest,
  UpdateCompanyProjectRequest,
  UpdateCompanyProjectOrderRequest,
  CompanyProjectResponse,
  CompanyProjectsResponse,
  ApiResponse,
} from '../types';

export const companyProjectService = {
  // Get all company projects (portfolio)
  getProjects: async (filters?: CompanyProjectFilters): Promise<CompanyProjectsResponse> => {
    const paramsWithSort = {
      ...filters,
      sort_by: filters?.sort_by || 'order',
      sort_order: filters?.sort_order || 'asc',
    };

    const response = await apiClient.get<CompanyProjectsResponse>(
      API_ENDPOINTS.ADMIN.COMPANY_PROJECTS,
      { params: paramsWithSort }
    );

    return response.data;
  },

  // Get single company project
  getProject: async (id: number): Promise<CompanyProjectResponse> => {
    const response = await apiClient.get<CompanyProjectResponse>(
      API_ENDPOINTS.ADMIN.COMPANY_PROJECT_DETAIL(id)
    );

    return response.data;
  },

  // Create company project
  createProject: async (
    data: CreateCompanyProjectRequest | FormData
  ): Promise<CompanyProjectResponse> => {
    const isFormData = data instanceof FormData;

    const response = await apiClient.post<CompanyProjectResponse>(
      API_ENDPOINTS.ADMIN.COMPANY_PROJECTS,
      data,
      isFormData
        ? {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        : undefined
    );

    return response.data;
  },

  // Update company project
  updateProject: async (
    id: number,
    data: UpdateCompanyProjectRequest | FormData
  ): Promise<CompanyProjectResponse> => {
    const isFormData = data instanceof FormData;

    if (isFormData) {
      const response = await apiClient.post<CompanyProjectResponse>(
        API_ENDPOINTS.ADMIN.COMPANY_PROJECT_UPDATE_WITH_FILES(id),
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    }

    const response = await apiClient.put<CompanyProjectResponse>(
      API_ENDPOINTS.ADMIN.COMPANY_PROJECT_DETAIL(id),
      data
    );

    return response.data;
  },

  // Delete company project
  deleteProject: async (id: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ADMIN.COMPANY_PROJECT_DETAIL(id));
  },

  // Update project order (for drag & drop)
  updateOrder: async (
    data: UpdateCompanyProjectOrderRequest
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.ADMIN.COMPANY_PROJECTS_ORDER,
      data
    );

    return response.data;
  },

  // Toggle project active status
  toggleActive: async (id: number): Promise<CompanyProjectResponse> => {
    const response = await apiClient.post<CompanyProjectResponse>(
      API_ENDPOINTS.ADMIN.COMPANY_PROJECT_TOGGLE_ACTIVE(id)
    );

    return response.data;
  },
};
